import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from "./core.js";
import { useObsinaI18n } from "./i18n.js";
import { cx, toneProperties } from "./utils.js";
export function Card({ as = "div", padding, emphasis = "default", onClick, disabled, children, className, style, ...props }) {
    const cardStyle = { ...style };
    if (padding)
        cardStyle["--obsina-card-padding"] = padding;
    if (onClick) {
        return (_jsx("button", { ...props, className: cx("obsina-card", className), "data-emphasis": emphasis, disabled: disabled, onClick: onClick, style: cardStyle, type: "button", children: _jsx("span", { children: children }) }));
    }
    const Component = as;
    return (_jsx(Component, { ...props, className: cx("obsina-card", className), "data-emphasis": emphasis, style: cardStyle, children: children }));
}
export function Badge({ tone = "neutral", solid = false, dot = false, children, className, style, ...props }) {
    return (_jsxs("span", { className: cx("obsina-badge", className), "data-solid": solid || undefined, style: { ...toneProperties(tone), ...style }, ...props, children: [dot && _jsx("span", { "aria-hidden": "true", className: "obsina-tone-dot" }), children] }));
}
export function Tag({ tone = "neutral", selected = false, onSelect, onRemove, accessibleLabel, dotColor, children, className, style, ...props }) {
    const { messages } = useObsinaI18n();
    const textLabel = accessibleLabel ?? (typeof children === "string" ? children : "tag");
    const content = (_jsxs(_Fragment, { children: [dotColor && (_jsx("span", { "aria-hidden": "true", className: "obsina-tone-dot", style: { background: dotColor } })), _jsx("span", { children: children })] }));
    return (_jsxs("span", { className: cx("obsina-tag", className), "data-selected": selected || undefined, style: { ...toneProperties(tone), ...style }, ...props, children: [onSelect ? (_jsx("button", { "aria-pressed": selected, className: "obsina-tag__content", onClick: onSelect, type: "button", children: content })) : (_jsx("span", { className: "obsina-tag__content", children: content })), onRemove && (_jsx("button", { "aria-label": messages.removeTag(textLabel), className: "obsina-tag__remove", onClick: onRemove, type: "button", children: _jsx("span", { "aria-hidden": "true", children: "\u00D7" }) }))] }));
}
const statusTone = {
    synced: "success",
    syncing: "warning",
    error: "danger",
    paused: "neutral",
    offline: "neutral",
    healthy: "success",
    partial: "warning",
    reauth: "danger",
    disconnected: "neutral",
};
export function StatusPill({ status = "synced", label, icon, announce = false, className, style, ...props }) {
    const { messages } = useObsinaI18n();
    return (_jsxs("span", { className: cx("obsina-status-pill", className), "data-status": status, role: announce ? "status" : undefined, style: { ...toneProperties(statusTone[status]), ...style }, ...props, children: [icon && (_jsx("span", { "aria-hidden": "true", className: "obsina-status-pill__icon", children: _jsx(Icon, { icon: icon, size: 13 }) })), _jsx("span", { "aria-hidden": "true", className: "obsina-status-pill__marker" }), _jsx("span", { children: label ?? messages.status[status] })] }));
}
const avatarColors = ["#A9421A", "#285FA8", "#246B45", "#37332C"];
function initialsFor(name) {
    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toLocaleUpperCase() ?? "")
        .join("");
}
function avatarColor(name) {
    let hash = 0;
    for (const character of name)
        hash = (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
    return avatarColors[hash % avatarColors.length] ?? avatarColors[0];
}
export function Avatar({ name, src, alt, size = 36, square = false, className, style, ...props }) {
    const avatarStyle = {
        "--obsina-avatar-size": `${size}px`,
        "--obsina-avatar-color": avatarColor(name),
        ...style,
    };
    return (_jsx("span", { "aria-label": src ? undefined : name || undefined, "aria-hidden": !src && !name ? true : undefined, className: cx("obsina-avatar", className), "data-square": square || undefined, role: !src && name ? "img" : undefined, style: avatarStyle, ...props, children: src ? _jsx("img", { alt: alt ?? name, src: src }) : initialsFor(name) }));
}
export function Table({ regionLabel, regionClassName, minWidth, className, style, children, ...props }) {
    const { messages } = useObsinaI18n();
    const tableStyle = { ...style };
    if (minWidth)
        tableStyle["--obsina-table-min-size"] = minWidth;
    return (_jsx("div", { "aria-label": regionLabel ?? messages.dataTable, className: cx("obsina-table-region", regionClassName), role: "region", tabIndex: 0, children: _jsx("table", { className: cx("obsina-table", className), style: tableStyle, ...props, children: children }) }));
}
export function TableHead(props) {
    return _jsx("thead", { ...props });
}
export function TableBody(props) {
    return _jsx("tbody", { ...props });
}
export function TableRow(props) {
    return _jsx("tr", { ...props });
}
export function TableHeader({ scope = "col", ...props }) {
    return _jsx("th", { scope: scope, ...props });
}
export function TableCell(props) {
    return _jsx("td", { ...props });
}
export function Progress({ value, max = 100, label, valueLabel, showValue = true, className, ...props }) {
    const { locale, messages } = useObsinaI18n();
    const safeMax = max > 0 ? max : 100;
    const normalized = value === undefined ? undefined : Math.min(Math.max(value, 0), safeMax);
    const fraction = normalized === undefined ? undefined : normalized / safeMax;
    const formatted = valueLabel ??
        (fraction === undefined
            ? undefined
            : new Intl.NumberFormat(locale, {
                style: "percent",
                maximumFractionDigits: 0,
            }).format(fraction));
    return (_jsxs("div", { className: cx("obsina-progress", className), ...props, children: [(Boolean(label) || Boolean(showValue && formatted)) && (_jsxs("div", { className: "obsina-progress__meta", children: [_jsx("span", { children: label }), showValue && formatted && _jsx("span", { children: formatted })] })), _jsx("div", { "aria-label": typeof label === "string" ? label : messages.progress, "aria-valuemax": safeMax, "aria-valuemin": 0, "aria-valuenow": normalized, "aria-valuetext": typeof formatted === "string" ? formatted : undefined, className: "obsina-progress__track", role: "progressbar", children: _jsx("div", { className: cx("obsina-progress__bar", normalized === undefined && "obsina-progress__bar--indeterminate"), style: normalized === undefined
                        ? undefined
                        : { inlineSize: `${fraction * 100}%` } }) })] }));
}
export function EmptyState({ title, description, icon, action, className, children, ...props }) {
    return (_jsxs("div", { className: cx("obsina-empty-state", className), ...props, children: [icon && (_jsx("span", { "aria-hidden": "true", className: "obsina-empty-state__icon", children: _jsx(Icon, { icon: icon, size: 24 }) })), _jsx("div", { className: "obsina-empty-state__title", children: title }), description && (_jsx("div", { className: "obsina-empty-state__description", children: description })), children, action] }));
}
export function Skeleton({ width, height, radius, label, className, style, ...props }) {
    const skeletonStyle = { ...style };
    if (width)
        skeletonStyle["--obsina-skeleton-width"] = width;
    if (height)
        skeletonStyle["--obsina-skeleton-height"] = height;
    if (radius)
        skeletonStyle["--obsina-skeleton-radius"] = radius;
    return (_jsx("span", { "aria-hidden": label ? undefined : true, "aria-label": label, className: cx("obsina-skeleton", className), role: label ? "status" : undefined, style: skeletonStyle, ...props }));
}
//# sourceMappingURL=display.js.map